import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit2, Camera, TrendingDown, Trophy, Users, LogOut, Gift, Podcast, Mail, ArrowLeft, Trash2, AlertTriangle } from 'lucide-react';
import { ProfileCard } from '../components/clubhouse/ProfileCard';
import { EligibilityChecklist } from '../components/clubhouse/EligibilityChecklist';
import { HandicapTracker } from '../components/clubhouse/HandicapTracker';
import { ProfilePhotoModal } from '../components/clubhouse/ProfilePhotoModal';
import { MemberModal } from '../components/clubhouse/MemberModal';
import { NotificationBell } from '../components/clubhouse/NotificationBell';
import { DeleteAccountModal } from '../components/clubhouse/DeleteAccountModal';
import { ClubhouseNav } from '../components/clubhouse/ClubhouseNav';
import { Toaster } from '../components/ui/toaster';
import { clubhouseApi, logoutClubhouse } from '../utils/clubhouseApi';
import './ClubhouseDashboard.css';



export const ClubhouseDashboard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [handicap, setHandicap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [modals, setModals] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    // Use cached user (non-sensitive) as a session hint. The httpOnly cookie
    // is the real auth; if it's missing or expired, API calls will 401 and
    // we'll redirect to login below.
    if (!localStorage.getItem('clubhouse_user')) {
      navigate('/clubhouse/login');
      return;
    }

    fetchData();
    fetchActiveModals();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [profileRes, handicapRes] = await Promise.all([
        clubhouseApi.get(`/api/clubhouse/profile/me`),
        clubhouseApi.get(`/api/clubhouse/handicap/me`)
      ]);

      setProfile(profileRes.data);
      setHandicap(handicapRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        await logoutClubhouse();
        navigate('/clubhouse/login');
      } else {
        setError('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logoutClubhouse();
    navigate('/clubhouse/login');
  };

  const handleDeleteAccount = async (confirmData) => {
    try {
      await clubhouseApi.post(
        `/api/clubhouse/account/delete`,
        confirmData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Clear cached user and redirect to deletion confirmation page.
      // Cookies were also cleared server-side via the account/delete endpoint.
      localStorage.removeItem('clubhouse_user');
      localStorage.removeItem('clubhouse_token'); // legacy cleanup
      navigate('/account-deleted');
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to delete account');
    }
  };

  const handleProfileUpdate = async (updatedData) => {
    try {
      const response = await clubhouseApi.put(
        `/api/clubhouse/profile/me`,
        updatedData,
      );
      setProfile(response.data);
      setEditMode(false);
    } catch (err) {
      setError('Failed to update profile');
    }
  };

  const handleHandicapUpdate = async (handicapIndex) => {
    try {
      const response = await clubhouseApi.post(
        `/api/clubhouse/handicap/me`,
        { handicap_index: parseFloat(handicapIndex) },
      );
      setHandicap(response.data);

      // Refresh profile to get updated eligibility
      const profileRes = await clubhouseApi.get(`/api/clubhouse/profile/me`);
      setProfile(profileRes.data);
    } catch (err) {
      setError('Failed to update handicap');
    }
  };

  const handlePhotoSave = async (imageData) => {
    try {
      // Upload the photo
      await clubhouseApi.post(
        `/api/clubhouse/profile/me/photo`,
        { image_data: imageData },
      );

      // Refetch profile to get updated photo URL and eligibility
      // This avoids full page reload and maintains state
      const profileRes = await clubhouseApi.get(`/api/clubhouse/profile/me`);
      setProfile(profileRes.data);
      setShowPhotoModal(false);
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
    }
  };

  const handlePhotoRemove = async () => {
    if (!window.confirm('Are you sure you want to remove your profile photo?')) {
      return;
    }

    try {
      // Delete the photo
      await clubhouseApi.delete(`/api/clubhouse/profile/me/photo`);

      // Refetch profile to get updated state and eligibility
      const profileRes = await clubhouseApi.get(`/api/clubhouse/profile/me`);
      setProfile(profileRes.data);
    } catch (error) {
      console.error('Error removing photo:', error);
      alert('Failed to remove photo. Please try again.');
    }
  };

  const fetchActiveModals = async () => {
    try {
      const response = await clubhouseApi.get(`/api/clubhouse/modals/active`);

      setModals(response.data);

      // Show first modal if any exist
      if (response.data.length > 0) {
        setActiveModal(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching modals:', error);
    }
  };

  const handleModalDismiss = (modalId) => {
    // Remove from modals list
    setModals(prev => prev.filter(m => m.id !== modalId));
    
    // Show next modal if available
    const remaining = modals.filter(m => m.id !== modalId);
    if (remaining.length > 0) {
      setActiveModal(remaining[0]);
    } else {
      setActiveModal(null);
    }
  };

  const handleModalReopen = (modalData) => {
    // If it's a full modal object, use it directly
    // Otherwise reconstruct from notification content
    if (modalData.primary_cta && modalData.secondary_cta) {
      setActiveModal(modalData);
    } else {
      // Reconstruct from notification content
      setActiveModal({
        id: modalData.modal_id || modalData.id,
        title: modalData.title || 'Announcement',
        headline: modalData.headline || '',
        body_text: modalData.body_text || '',
        icon: modalData.icon || 'megaphone',
        show_progress_checklist: false,
        primary_cta: { text: 'Close', action_type: 'close' },
        secondary_cta: { text: 'Dismiss', action_type: 'close' }
      });
    }
  };

  return (
    <div className="clubhouse-dashboard">
      {/* Navigation */}
      <ClubhouseNav onLogout={handleLogout} userProfile={profile} />

      {/* Header */}
      <header className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div>
              <h1>Member Clubhouse</h1>
              <p>Welcome back, {
                profile?.display_preference === 'username' && profile?.username
                  ? profile.username
                  : profile?.first_name || profile?.username || 'Member'
              }!</p>
            </div>
            <div className="header-utils">
              <NotificationBell onModalReopen={handleModalReopen} />
            </div>
          </div>
        </div>
      </header>

      <div className="container dashboard-container">
        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}

        {/* Main Grid */}
        <div className={`dashboard-grid ${editMode ? 'edit-mode-active' : ''}`}>
          {/* Left Column - Profile Card (expands in edit mode) */}
          <div className={`dashboard-left ${editMode ? 'edit-mode-expanded' : ''}`}>
            {/* Profile Card */}
            <ProfileCard 
              profile={profile}
              editMode={editMode}
              onEdit={() => setEditMode(true)}
              onSave={handleProfileUpdate}
              onCancel={() => setEditMode(false)}
              onOpenPhotoModal={() => setShowPhotoModal(true)}
              onDeleteAccount={() => setShowDeleteModal(true)}
            />

            {/* Eligibility Checklist - Hidden in edit mode */}
            <div className={`module-animate ${editMode ? 'module-hidden' : 'module-visible'}`}>
              <EligibilityChecklist 
                eligibility={profile?.eligibility}
                profile={profile}
              />
            </div>
          </div>

          {/* Right Column - Hidden in edit mode */}
          <div className={`dashboard-right module-animate ${editMode ? 'module-hidden' : 'module-visible'}`}>
            {/* Handicap Tracker */}
            <HandicapTracker 
              handicap={handicap}
              onUpdate={handleHandicapUpdate}
            />

            {/* Contest Eligibility */}
            <div className="stats-card">
              <h3>Contest & Giveaway Eligibility</h3>
              
              <div className="eligibility-section">
                <h4 className="section-title">GHIN Contests</h4>
                <div className="stats-grid">
                  <div className="stat-item">
                    <TrendingDown className="stat-icon" />
                    <div>
                      <div className="stat-value">Monthly Lowest</div>
                      <div className={`stat-label ${profile?.eligibility?.is_eligible_for_monthly_lowest_hdcp_contest ? 'eligible' : 'not-eligible'}`}>
                        {profile?.eligibility?.is_eligible_for_monthly_lowest_hdcp_contest 
                          ? '✓ Eligible' 
                          : '✗ Not Eligible'}
                      </div>
                    </div>
                  </div>
                  <div className="stat-item">
                    <Trophy className="stat-icon" />
                    <div>
                      <div className="stat-value">Most Improved</div>
                      <div className={`stat-label ${profile?.eligibility?.is_eligible_for_quarterly_improvement_contest ? 'eligible' : 'not-eligible'}`}>
                        {profile?.eligibility?.is_eligible_for_quarterly_improvement_contest 
                          ? '✓ Eligible' 
                          : '✗ Not Eligible'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="eligibility-section">
                <h4 className="section-title">General Giveaways</h4>
                <div className="stats-grid">
                  <div className="stat-item">
                    <Gift className="stat-icon" />
                    <div>
                      <div className="stat-value">Regular Series</div>
                      <div className={`stat-label ${profile?.eligibility?.is_eligible_for_regular_series_giveaway ? 'eligible' : 'not-eligible'}`}>
                        {profile?.eligibility?.is_eligible_for_regular_series_giveaway 
                          ? '✓ Eligible' 
                          : '✗ Not Eligible'}
                      </div>
                    </div>
                  </div>
                  <div className="stat-item">
                    <Podcast className="stat-icon" />
                    <div>
                      <div className="stat-value">Podcast Giveaways</div>
                      <div className={`stat-label ${profile?.eligibility?.is_eligible_for_podcast_giveaway ? 'eligible' : 'not-eligible'}`}>
                        {profile?.eligibility?.is_eligible_for_podcast_giveaway 
                          ? '✓ Eligible' 
                          : '✗ Not Eligible'}
                      </div>
                    </div>
                  </div>
                  <div className="stat-item">
                    <Mail className="stat-icon" />
                    <div>
                      <div className="stat-value">Newsletter</div>
                      <div className={`stat-label ${profile?.eligibility?.is_eligible_for_newsletter_giveaway ? 'eligible' : 'not-eligible'}`}>
                        {profile?.eligibility?.is_eligible_for_newsletter_giveaway 
                          ? '✓ Eligible' 
                          : '✗ Not Eligible'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="eligibility-info">
                <p className="info-text">
                  <strong>GHIN Contests:</strong> Requires complete profile + handicap
                </p>
                <p className="info-text">
                  <strong>General Giveaways:</strong> Requires complete profile + shipping address
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Photo Modal - Rendered at top level for full-screen overlay */}
      <ProfilePhotoModal
        isOpen={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        onSave={handlePhotoSave}
        onRemove={handlePhotoRemove}
        currentPhotoUrl={profile?.profile_photo_url}
      />
      
      {/* Member Modal */}
      {activeModal && (
        <MemberModal
          modal={activeModal}
          onClose={() => setActiveModal(null)}
          onDismiss={handleModalDismiss}
        />
      )}
      
      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirmDelete={handleDeleteAccount}
        userEmail={profile?.email}
      />
      
      <Toaster />
    </div>
  );
};
